import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_LINKS } from "@/data/content";
import { EASE } from "@/components/Motion";

const NavItem = ({ link, className, testId, onClick }) =>
  link.href.includes("#") ? (
    <a href={link.href} data-testid={testId} className={className} onClick={onClick}>
      {link.label}
    </a>
  ) : (
    <Link to={link.href} data-testid={testId} className={className} onClick={onClick}>
      {link.label}
    </Link>
  );

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const solid = scrolled || pathname !== "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const left = NAV_LINKS.slice(0, 3);
  const right = NAV_LINKS.slice(3);
  const linkCls = "text-[13px] tracking-[0.18em] uppercase font-sans hover:opacity-60 transition-opacity duration-300";

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 1, delay: 0.4, ease: EASE }}
        className={`fixed top-0 inset-x-0 z-[80] transition-all duration-500 ${
          solid ? "bg-cream/85 backdrop-blur-md text-ink shadow-[0_1px_0_0_#E8E2D5]" : "bg-transparent text-cream"
        }`}
        data-testid="main-navbar"
      >
        <nav className="mx-auto max-w-[1400px] px-6 lg:px-12 h-[72px] grid grid-cols-[1fr_auto_1fr] items-center">
          <div className="hidden lg:flex items-center gap-8">
            {left.map((l) => (
              <NavItem key={l.id} link={l} testId={`nav-${l.id}`} className={linkCls} />
            ))}
          </div>
          <button
            className="lg:hidden justify-self-start p-2 -ml-2"
            onClick={() => setOpen(true)}
            aria-label="Open menu"
            data-testid="nav-hamburger-btn"
          >
            <Menu size={22} strokeWidth={1.5} />
          </button>

          <Link to="/" data-testid="nav-logo" className="justify-self-center text-center leading-none">
            <span className="font-serif text-[26px] sm:text-[30px] font-medium tracking-wide">
              Forco <span className="italic">Amore</span>
            </span>
            <span className="block text-[9px] tracking-[0.5em] uppercase mt-1 opacity-70">Fusion Ristorante</span>
          </Link>

          <div className="hidden lg:flex items-center justify-end gap-8">
            {right.map((l) => (
              <NavItem key={l.id} link={l} testId={`nav-${l.id}`} className={linkCls} />
            ))}
            <Link
              to="/reservations"
              data-testid="nav-reserve-btn"
              className={`ml-2 rounded-full px-6 py-2.5 text-[13px] tracking-[0.14em] uppercase transition-colors duration-300 ${
                solid ? "bg-wine text-cream hover:bg-winedark" : "bg-cream text-ink hover:bg-white"
              }`}
            >
              Reserve
            </Link>
          </div>
          <div className="lg:hidden" />
        </nav>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="fixed inset-0 z-[100] bg-wine text-cream flex flex-col"
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between px-6 h-[72px]">
              <span className="font-serif text-2xl italic">Forco Amore</span>
              <button onClick={() => setOpen(false)} aria-label="Close menu" data-testid="mobile-menu-close" className="p-2">
                <X size={26} strokeWidth={1.5} />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-8 gap-2">
              {NAV_LINKS.map((l, i) => (
                <motion.div
                  key={l.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + i * 0.07, duration: 0.6, ease: EASE }}
                >
                  <NavItem
                    link={l}
                    testId={`mobile-nav-${l.id}`}
                    onClick={() => setOpen(false)}
                    className="block font-serif text-4xl sm:text-5xl py-3 border-b border-cream/15 hover:pl-4 transition-all duration-300"
                  />
                </motion.div>
              ))}
            </nav>
            <p className="px-8 pb-10 text-cream/60 text-sm tracking-widest uppercase">Italian Soul • Indian Heart • Made With Amore</p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
